import '@/assets/scss/main.scss'
import { useSiteConfigStore } from '@/stores/config/site'
import { useCartConfigStore } from '@/stores/config/cart'
import { useFooterStore } from '@/stores/config/footer'

const brandName = import.meta.env.VITE_BRAND_NAME

const loadConfig = async () => {
  const siteConfig = useSiteConfigStore()
  const cartConfig = useCartConfigStore()
  const footerStore = useFooterStore()
  const response = await fetch(`/config/${brandName}.json`)
  const config = await response.json()

  if (!config.productConfig || typeof config.productConfig !== 'object') {
    config.productConfig = {}
  }

  if (!config.shoppingOptions) {
    config.shoppingOptions = {
      enableClientAddresses: false,
      enableAddressMapSelection: false,
      enablePickupAtStore: false
    }
  }

  if (!config.pricingRules) {
    config.pricingRules = {
      enableDynamicTierPricing: false,
      thresholdAmountForTierUpgrade: 0,
      toPriceKey: 'price_2',
      excludedUserByProfileKey: [],
      excludedUserTypes: [],
      successMessage:
        '¡Felicidades! Tu carrito supera los <strong>##price</strong>, accedes a precios de fábrica. ¡Disfruta tus ahorros!'
    }
  }

  // Filtrar datos específicos para cada store
  const siteData = {
    canSelectOffice: config.generalSettings.canSelectOffice || false,
    homeCarouselWithProductsBrands: config.generalSettings.homeCarouselWithProductsBrands || false,
    clientFormatDataOnSearch: config.generalSettings.clientFormatDataOnSearch || [
      'name',
      'client_num',
      'price_id'
    ],
    brandImagesShared: config.generalSettings.brandImagesShared,
    b2c: config.b2c,
    userLocationLogic: config.generalSettings.userLocationLogic,
    whatsappNumber: config.generalSettings.whatsappNumber,
    brandName: config.brandName,
    titleBase: config.titleBase,
    onlyQuotations: config.generalSettings.onlyQuotations,
    minimumPurchasePrice: config.generalSettings.minimumPurchasePrice,
    displayWholesalePrices: config.generalSettings.displayWholesalePrices,
    displayProductPageCatalog: config.generalSettings.displayProductPageCatalog,
    displayProductReleaseDate: config.generalSettings.displayProductReleaseDate,
    productConfig: config.generalSettings.productConfig,
    assetsFolderPrefix: config.assetsFolderPrefix,
    colors: config.colors,
    pricingRules: config.pricingRules,
    shoppingOptions: config.shoppingOptions,
    registerConfig: config.generalSettings.registerConfig
  }

  const cartData = config.cartConfig
  const footerData = config.footer

  // Enviar datos filtrados a los stores correspondientes
  siteConfig.setSiteConfig(siteData)
  cartConfig.setCartConfig(cartData)
  footerStore.setFooterConfig(footerData)
}

// Configura los colores basados en la marca
const setBrandStyles = () => {
  const root = document.documentElement
  const siteConfig = useSiteConfigStore()
  const colors = siteConfig.getColors

  const requireLightenAndDarken = [
    'primary',
    'primary_admin',
    'secondary',
    'secondary_admin',
    'tertiary',
    'tertiary_admin',
    'tertiary_alternative',
    'text_app_alternative'
  ]

  Object.entries(colors).forEach(([key, value]) => {
    const cssVar = `--color-${key.replace(/_/g, '-')}`
    root.style.setProperty(cssVar, value)

    if (requireLightenAndDarken.includes(key)) {
      lightenColor(value, 10).then((lightenValue) =>
        root.style.setProperty(`${cssVar}-light`, lightenValue)
      )
      darkenColor(value, 10).then((darkenValue) =>
        root.style.setProperty(`${cssVar}-dark`, darkenValue)
      )
    }
  })
}

const darkenColor = async (hex, percent) => {
  // Asegurarse de que el hex esté en el formato correcto
  const num = parseInt(hex.slice(1), 16)

  // Calcular los valores RGB
  let R = (num >> 16) & 0xff
  let G = (num >> 8) & 0xff
  let B = num & 0xff

  // Aplicar el oscurecimiento proporcional
  R = Math.round(R * (1 - percent / 100))
  G = Math.round(G * (1 - percent / 100))
  B = Math.round(B * (1 - percent / 100))

  // Asegurarse de que los valores estén dentro del rango 0-255
  R = Math.max(0, Math.min(255, R))
  G = Math.max(0, Math.min(255, G))
  B = Math.max(0, Math.min(255, B))

  // Convertir de nuevo a formato hexadecimal
  return `#${((1 << 24) + (R << 16) + (G << 8) + B).toString(16).slice(1).toUpperCase()}`
}

const lightenColor = async (hex, percent) => {
  const num = parseInt(hex.replace('#', ''), 16),
    amt = Math.round(2.55 * percent),
    R = (num >> 16) + amt,
    G = ((num >> 8) & 0x00ff) + amt,
    B = (num & 0x0000ff) + amt
  return (
    '#' +
    (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
  )
}

// Llama a la función con el nombre de la marca
export const setSiteConfig = async () => {
  await loadConfig()
  await setBrandStyles()
  return true
}
